import { useTranslate } from "@refinedev/core";
import { Settings } from "lucide-react";

import { type Dichtheid } from "./Gezondheidsring";
import { onderdeelStatus } from "./dierStatus";
import { COMPONENT_PER_SLEUTEL } from "./dierComponenten";
import { GewichtOnderdeel } from "./GewichtOnderdeel";
import { VoedingOnderdeel } from "./VoedingOnderdeel";
import { AllergieenOnderdeel } from "./AllergieenOnderdeel";
import { ChipPaspoortOnderdeel } from "./ChipPaspoortOnderdeel";
import {
  BijzonderhedenOnderdeel, DiersoortOnderdeel, GeslachtOnderdeel, KenmerkenOnderdeel, LeeftijdOnderdeel,
  RasOnderdeel,
} from "./DierveldOnderdelen";
import {
  EntingenOnderdeel, MedicatieOnderdeel, OntvlooiingOnderdeel, OntwormingOnderdeel,
} from "./MedischeOnderdelen";
import {
  DagprijsOnderdeel, DierenartsOnderdeel, GedragOnderdeel, VerzorgingOnderdeel,
} from "./OverigeOnderdelen";
import {
  Boogmeter,
  Fotokern, Gesplitst, HalveMeter, Kwadranten, Prijskaart, Radiaal, Rondom, Satellieten, Statuslijst,
  TweeKolommen,
  Waardenboog,
} from "./tegelVormen";
import { toontActie, type SamengesteldeTegel } from "./samengesteldeTegels";
import type { PensionDier } from "../pensionKlantTypes";

/**
 * Een zelf samengestelde tegel op het dieroverzicht.
 *
 * <p>Hier komt alles samen: de tegel zegt welke onderdelen en welke vorm, de componentbibliotheek zegt
 * wat een onderdeel is, en het onderdeel haalt zijn eigen gegevens op. Deze component doet niet meer dan
 * de vertaling van sleutel naar component -- en dat is bewust het enige wat hij doet, want elke regel
 * kennis die hier landt is kennis die op twee plekken moet kloppen.
 */

/**
 * Het component bij een sleutel.
 *
 * <p>Alle onderdelen lezen uit wat het dierscherm toch al heeft, of halen hun eigen bron op. Verzorging
 * krijgt niets mee: borstelen en wassen staan op de reserveringsregel, en welke boeking bedoeld is weet
 * een diertegel niet. Het onderdeel zegt dan "nog niets geregistreerd", en dat is waar zolang de tegel
 * geen boeking kent.
 */
function Onderdeel({ sleutel, dier, vandaag }: { sleutel: string; dier: PensionDier; vandaag: string }) {
  switch (sleutel) {
    case "gewicht":
      return <GewichtOnderdeel dierId={dier.id} />;
    case "voeding":
      return <VoedingOnderdeel dierId={dier.id} />;
    case "allergieen":
      return <AllergieenOnderdeel dierId={dier.id} />;
    case "chipPaspoort":
      return <ChipPaspoortOnderdeel dierId={dier.id} />;
    case "vaccinaties":
      return <EntingenOnderdeel stand={dier.stand ?? null} />;
    case "ontworming":
      return <OntwormingOnderdeel stand={dier.stand ?? null} />;
    case "ontvlooiing":
      return <OntvlooiingOnderdeel stand={dier.stand ?? null} />;
    case "medicatie":
      return <MedicatieOnderdeel stand={dier.stand ?? null} />;
    case "gedrag":
      return <GedragOnderdeel gedrag={dier.gedrag} />;
    case "dierenarts":
      return <DierenartsOnderdeel praktijk={dier.dierenarts} />;
    case "dagprijs":
      return <DagprijsOnderdeel prijs={dier.prijs} />;
    case "verzorging":
      return <VerzorgingOnderdeel />;
    case "diersoort":
      return <DiersoortOnderdeel dier={dier} />;
    case "ras":
      return <RasOnderdeel dier={dier} />;
    case "geslacht":
      return <GeslachtOnderdeel dier={dier} />;
    case "leeftijd":
      return <LeeftijdOnderdeel dier={dier} vandaag={vandaag} />;
    case "kenmerken":
      return <KenmerkenOnderdeel dier={dier} />;
    case "bijzonderheden":
      return <BijzonderhedenOnderdeel dier={dier} />;
    default:
      // Een sleutel die de bibliotheek niet kent hoort door het inlezen al te zijn weggegooid; komt hij
      // er toch door, dan is niets tonen beter dan een leeg vak waar niemand de bedoeling van raadt.
      return null;
  }
}

export function EigenTegel({
  tegel,
  dier,
  foto,
  dichtheid,
  vandaag,
  onInstellen,
  onOpenen,
}: {
  tegel: SamengesteldeTegel;
  dier: PensionDier;
  /** Vandaag als yyyy-MM-dd, voor de leeftijd. */
  vandaag: string;
  /** De foto van dit dier; de ronde tegel kan hem in het midden zetten. */
  foto?: string;
  /** De dichtheid die het systeem koos; de tegel mag hem overrulen met een vaste maat. */
  dichtheid: Dichtheid;
  onInstellen: () => void;
  onOpenen?: () => void;
}) {
  const t = useTranslate();
  const maat = tegel.automatischSchalen ? dichtheid : tegel.vasteDichtheid;
  const naam = tegel.naam.trim() || t(`dierTegel.vorm.${tegel.vorm}`);

  const onderdelen = (
    <>
      {tegel.componenten.map((sleutel) => (
        // De snelle actie staat per onderdeel in de tegel; het onderdeel zelf kent die keuze niet en
        // weet alleen van rechten. Hier wordt de knop verborgen, niet het onderdeel -- de waarde blijft
        // staan, want wie niet mag registreren moet nog steeds kunnen zien wat er is.
        <div key={sleutel} className={toontActie(tegel, sleutel) ? undefined : "dt-geenactie"}>
          <Onderdeel sleutel={sleutel} dier={dier} vandaag={vandaag} />
        </div>
      ))}
    </>
  );

  // Eén blokje per gekozen onderdeel, in dezelfde volgorde als de lijst eronder, met de kleur van dat
  // onderdeel. Zo is aan de ring te zien wat er speelt en aan de lijst welk blokje wat is.
  const segmenten = tegel.componenten.map((sleutel) => ({
    sleutel,
    label: t(COMPONENT_PER_SLEUTEL[sleutel].label),
    status: onderdeelStatus(sleutel, dier, new Date()),
  }));
  const props = { onderdelen, dichtheid: maat, onOpenen };
  // Dezelfde foto voor elke vorm met een midden, zodat "toon foto" overal hetzelfde doet. De ronde
  // tegel en de halve meter krijgen hem apart mee: daar zit hij binnen de ring.
  const fotoKern = tegel.fotoInMidden && foto ? <Fotokern foto={foto} /> : undefined;
  const inhoud = (() => {
    switch (tegel.vorm) {
      case "ring":
        // De foto in het midden met de waarden eromheen, elk vast aan zijn eigen ringsegment. Hier
        // mag elk gekozen onderdeel meedoen en niet alleen de medische: het segment krijgt de status
        // van zijn eigen onderdeel, en een gewicht dat nooit gemeten is staat grijs in plaats van dat
        // het de ring iets laat beweren wat er niet in staat.
        return <Rondom segmenten={segmenten} foto={tegel.fotoInMidden ? foto : null} {...props} />;
      case "halvemeter":
        return <HalveMeter segmenten={segmenten} foto={tegel.fotoInMidden ? foto : null} {...props} />;
      case "boogmeter":
        return <Boogmeter segmenten={segmenten} {...props} />;
      case "satellieten":
        return <Satellieten kern={fotoKern} {...props} />;
      case "gesplitst":
        return <Gesplitst kern={fotoKern} {...props} />;
      case "radiaal":
        return <Radiaal kern={fotoKern ?? <span>{dier.naam}</span>} {...props} />;
      case "prijskaart":
        return <Prijskaart kern={fotoKern} {...props} />;
      case "tweekolommen":
        return <TweeKolommen kern={fotoKern} {...props} />;
      case "waardenboog":
        return <Waardenboog kern={fotoKern} {...props} />;
      case "kwadranten":
        return <Kwadranten kern={fotoKern} {...props} />;
      case "statuslijst":
      default:
        return <Statuslijst kern={fotoKern} {...props} />;
    }
  })();

  return (
    <section
      className={`pk-tegel${tegel.toonHerkomst ? "" : " dt-geenherkomst"}`}
      data-tegel={`eigen-${tegel.id}`}
      data-breedte={tegel.breedte}
      aria-label={naam}
    >
      <header className="pk-tegel__kop">
        <h3>{naam}</h3>
        <button
          type="button"
          className="pk-tegel__instellen"
          data-testid={`eigen-instellen-${tegel.id}`}
          aria-label={t("dierTegel.bouwer.instellen", { args: [naam] })}
          onClick={onInstellen}
        >
          <Settings size={15} aria-hidden="true" />
        </button>
      </header>
      {tegel.componenten.length === 0 ? (
        <p className="pk-tegel__leeg">{t("dierTegel.bouwer.nogNiets")}</p>
      ) : (
        inhoud
      )}
    </section>
  );
}
